import React, { useState, useEffect } from 'react';
import { 
  TextField, Button, FormControl, InputLabel, Select, MenuItem, Checkbox, ListItemText, 
  Box, Grid, Snackbar, Alert, Typography, Paper, List, ListItem, ListItemSecondaryAction,
  IconButton, Divider, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Switch, Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import api from '../utils/api'; // 改用我們的API工具

function AutoRecordConfig() {
  const [rules, setRules] = useState([]);
  const [channels, setChannels] = useState([]); 
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentRule, setCurrentRule] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState(null);
  
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // 頁面加載時獲取規則列表和頻道列表
  useEffect(() => {
    document.title = '自動錄製設定';
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [rulesResponse, channelsResponse] = await Promise.all([
        api.get('/auto-record-rules'),
        api.get('/channels')
      ]);

      setRules(rulesResponse.data || []);
      // 介面仍以 {value, label} 形式消費，將 v2 的 {id, name} 映射過來。
      const normalized = (channelsResponse.data || []).map(c => ({
        value: c.id,
        label: c.name,
        has_replay: !!c.has_replay,
      }));
      setChannels(normalized);
    } catch (err) {
      console.error('獲取數據失敗:', err);
      setSnackbar({
        open: true,
        message: '獲取數據失敗',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  const handleAddRule = () => {
    setCurrentRule({
      name: '',
      keywords: [''],
      exclude_keywords: [''],
      channels: [],
      is_active: true
    });
    setOpenDialog(true);
  };

  const handleEditRule = (rule) => {
    // 確保排除關鍵字字段存在
    const ruleWithExcludeKeywords = {
      ...rule,
      exclude_keywords: rule.exclude_keywords || ['']
    };
    setCurrentRule(ruleWithExcludeKeywords);
    setOpenDialog(true);
  };

  const handleDeleteRule = (rule) => {
    setRuleToDelete(rule);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteRule = async () => {
    if (!ruleToDelete) return;
    
    try {
      await api.delete(`/auto-record-rules/${ruleToDelete.id}`);
      setSnackbar({
        open: true,
        message: `規則 "${ruleToDelete.name}" 已刪除`,
        severity: 'success'
      });
      fetchData();
    } catch (err) {
      console.error('刪除規則失敗:', err);
      setSnackbar({
        open: true,
        message: '刪除規則失敗',
        severity: 'error'
      });
    } finally {
      setDeleteConfirmOpen(false);
      setRuleToDelete(null);
    }
  };

  const handleToggleRuleStatus = async (rule) => {
    try {
      await api.patch(`/auto-record-rules/${rule.id}`, {
        is_active: !rule.is_active
      });
      
      setSnackbar({
        open: true,
        message: `規則 "${rule.name}" 已${!rule.is_active ? '啟用' : '禁用'}`,
        severity: 'success'
      });
      
      fetchData();
    } catch (err) {
      console.error('切換規則狀態失敗:', err);
      setSnackbar({
        open: true,
        message: '切換規則狀態失敗',
        severity: 'error'
      });
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentRule(null);
  };

  const handleDialogSave = async () => {
    // 驗證表單
    if (!currentRule.name || currentRule.name.trim() === '') {
      setSnackbar({
        open: true,
        message: '請輸入規則名稱',
        severity: 'error'
      });
      return;
    }
    
    const filteredKeywords = currentRule.keywords.filter(k => k.trim() !== '');
    if (filteredKeywords.length === 0) {
      setSnackbar({
        open: true,
        message: '請至少輸入一個關鍵字',
        severity: 'error'
      });
      return;
    }
    
    if (currentRule.channels.length === 0) {
      setSnackbar({
        open: true,
        message: '請至少選擇一個頻道',
        severity: 'error'
      });
      return;
    }
    
    // 過濾掉空的排除關鍵字
    const filteredExcludeKeywords = currentRule.exclude_keywords.filter(k => k.trim() !== '');
    
    // 準備提交的數據
    const ruleData = {
      name: currentRule.name.trim(),
      keywords: filteredKeywords,
      exclude_keywords: filteredExcludeKeywords,
      channels: currentRule.channels,
      is_active: currentRule.is_active
    };
    
    try {
      if (currentRule.id) {
        // 更新現有規則
        await api.put(`/auto-record-rules/${currentRule.id}`, ruleData);
        setSnackbar({
          open: true,
          message: `規則 "${currentRule.name}" 已更新`,
          severity: 'success'
        });
      } else {
        // 創建新規則
        await api.post('/auto-record-rules', ruleData);
        setSnackbar({
          open: true,
          message: `規則 "${currentRule.name}" 已創建`,
          severity: 'success'
        });
      }
      
      // 重新獲取最新的規則列表
      fetchData();
      handleCloseDialog();
    } catch (err) {
      console.error('保存規則失敗:', err);
      setSnackbar({
        open: true,
        message: '保存規則失敗',
        severity: 'error'
      });
    }
  };

  const handleRunCheck = async () => {
    try {
      // AIP-136 自訂動作：POST /auto-record-rules:scan
      const res = await api.post('/auto-record-rules:scan');
      const inserted = res.data?.inserted ?? 0;
      setSnackbar({
        open: true,
        message: `自動檢查完成，新增 ${inserted} 則節目`,
        severity: 'success'
      });
    } catch (err) {
      console.error('啟動自動檢查失敗:', err);
      setSnackbar({
        open: true,
        message: '啟動自動檢查失敗',
        severity: 'error'
      });
    }
  };

  // 處理規則表單中的關鍵字
  const handleKeywordChange = (index, value) => {
    const newKeywords = [...currentRule.keywords];
    newKeywords[index] = value;
    setCurrentRule({...currentRule, keywords: newKeywords});
  };

  const handleAddKeyword = () => {
    setCurrentRule({
      ...currentRule, 
      keywords: [...currentRule.keywords, '']
    });
  };

  const handleRemoveKeyword = (index) => {
    const newKeywords = [...currentRule.keywords];
    newKeywords.splice(index, 1);
    setCurrentRule({...currentRule, keywords: newKeywords});
  };

  // 處理規則表單中的排除關鍵字
  const handleExcludeKeywordChange = (index, value) => {
    const newExcludeKeywords = [...currentRule.exclude_keywords];
    newExcludeKeywords[index] = value;
    setCurrentRule({...currentRule, exclude_keywords: newExcludeKeywords});
  };

  const handleAddExcludeKeyword = () => {
    setCurrentRule({
      ...currentRule, 
      exclude_keywords: [...currentRule.exclude_keywords, '']
    });
  };

  const handleRemoveExcludeKeyword = (index) => {
    const newExcludeKeywords = [...currentRule.exclude_keywords];
    newExcludeKeywords.splice(index, 1);
    setCurrentRule({...currentRule, exclude_keywords: newExcludeKeywords});
  };

  // 處理規則表單中的頻道選擇
  const handleChannelChange = (event) => {
    setCurrentRule({
      ...currentRule,
      channels: event.target.value
    });
  };

  const handleSelectAllChannels = () => {
    if (currentRule.channels.length === channels.length) {
      setCurrentRule({
        ...currentRule,
        channels: []
      });
    } else {
      setCurrentRule({
        ...currentRule,
        channels: channels.map(channel => channel.value)
      });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" component="h2">自動錄製設定</Typography>
          <Box>
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={<AddIcon />}
              onClick={handleAddRule}
              sx={{ mr: 1 }}
            >
              新增規則
            </Button>
            <Button 
              variant="outlined"
              color="secondary"
              startIcon={<PlayArrowIcon />}
              onClick={handleRunCheck}
            >
              立即檢查
            </Button>
          </Box>
        </Grid>
        
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              系統將每小時自動檢查符合以下規則的節目，匹配成功的節目將自動加入錄製清單
            </Typography>
            
            {rules.length === 0 ? (
              <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                {loading ? '載入中...' : '尚未設定任何自動錄製規則'}
              </Typography>
            ) : (
              <List>
                {rules.map((rule) => (
                  <React.Fragment key={rule.id}>
                    <ListItem 
                      sx={{ 
                        bgcolor: rule.is_active ? 'background.paper' : 'action.hover',
                        opacity: rule.is_active ? 1 : 0.7
                      }}
                    >
                      <Box sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                            {rule.name}
                            <Switch
                              checked={rule.is_active}
                              onChange={() => handleToggleRuleStatus(rule)}
                              size="small"
                              sx={{ ml: 1 }}
                            />
                          </Typography>
                        </Box>
                        
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            關鍵字：
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                            {rule.keywords.map((keyword, idx) => (
                              <Chip key={idx} label={keyword} size="small" />
                            ))}
                          </Box>
                        </Box>
                        
                        {rule.exclude_keywords && rule.exclude_keywords.length > 0 && rule.exclude_keywords[0] !== '' && (
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              排除關鍵字：
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                              {rule.exclude_keywords.map((keyword, idx) => (
                                <Chip key={idx} label={keyword} size="small" color="error" />
                              ))}
                            </Box>
                          </Box>
                        )}
                        
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            頻道：
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                            {rule.channels.map((channelId) => {
                              const channel = channels.find(ch => ch.value === channelId);
                              return (
                                <Chip 
                                  key={channelId} 
                                  label={channel ? channel.label : channelId} 
                                  size="small"
                                  variant="outlined"
                                />
                              );
                            })}
                          </Box>
                        </Box>
                      </Box>
                      
                      <ListItemSecondaryAction>
                        <Tooltip title="編輯">
                          <IconButton edge="end" aria-label="edit" onClick={() => handleEditRule(rule)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="刪除">
                          <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteRule(rule)}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>
      
      {/* 編輯/新增規則對話框 */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{currentRule?.id ? '編輯規則' : '新增規則'}</DialogTitle>
        <DialogContent>
          {currentRule && (
            <Grid container spacing={2} sx={{ pt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  label="規則名稱"
                  fullWidth
                  value={currentRule.name}
                  onChange={(e) => setCurrentRule({...currentRule, name: e.target.value})}
                  required
                />
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle1">關鍵字</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  系統將檢查節目的標題、描述、摘要和演出者名稱是否包含以下任一關鍵字
                </Typography>
                
                {currentRule.keywords.map((keyword, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <TextField
                      label={`關鍵字 ${index + 1}`}
                      variant="outlined"
                      value={keyword}
                      onChange={(event) => handleKeywordChange(index, event.target.value)}
                      fullWidth
                    />
                    <Button 
                      onClick={() => handleRemoveKeyword(index)} 
                      variant="outlined" 
                      color="error" 
                      sx={{ ml: 2 }}
                      disabled={currentRule.keywords.length <= 1}
                    >
                      刪除
                    </Button>
                  </Box>
                ))}
                
                <Button 
                  onClick={handleAddKeyword} 
                  variant="outlined" 
                  startIcon={<AddIcon />}
                >
                  添加關鍵字
                </Button>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ mt: 2 }}>排除關鍵字</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  如果節目包含以下任一關鍵字，即使符合上述關鍵字也會被排除
                </Typography>
                
                {currentRule.exclude_keywords.map((keyword, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <TextField
                      label={`排除關鍵字 ${index + 1}`}
                      variant="outlined"
                      value={keyword}
                      onChange={(event) => handleExcludeKeywordChange(index, event.target.value)}
                      fullWidth
                    />
                    <Button 
                      onClick={() => handleRemoveExcludeKeyword(index)} 
                      variant="outlined" 
                      color="error" 
                      sx={{ ml: 2 }}
                      disabled={currentRule.exclude_keywords.length <= 1}
                    >
                      刪除
                    </Button>
                  </Box>
                ))}
                
                <Button 
                  onClick={handleAddExcludeKeyword} 
                  variant="outlined" 
                  startIcon={<AddIcon />}
                >
                  添加排除關鍵字
                </Button>
              </Grid>
              
              <Grid item xs={12} md={6} sx={{ mt: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>頻道</InputLabel>
                  <Select
                    label="頻道"
                    multiple
                    value={currentRule.channels}
                    onChange={handleChannelChange}
                    renderValue={(selected) => {
                      return selected.map(value => {
                        const channel = channels.find(ch => ch.value === value);
                        return channel ? channel.label : value;
                      }).join(', ');
                    }}
                  >
                    {channels.map((channel) => (
                      <MenuItem key={channel.value} value={channel.value}>
                        <Checkbox checked={currentRule.channels.includes(channel.value)} />
                        <ListItemText primary={channel.label} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6} sx={{ mt: 2 }}>
                <Button 
                  onClick={handleSelectAllChannels} 
                  variant="outlined" 
                  color="primary" 
                  sx={{ height: '56px', width: '100%' }}
                >
                  {currentRule.channels.length === channels.length ? '取消全選' : '全選頻道'}
                </Button>
              </Grid>
              
              <Grid item xs={12} sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                <Typography sx={{ mr: 2 }}>啟用此規則</Typography>
                <Switch
                  checked={currentRule.is_active}
                  onChange={(e) => setCurrentRule({...currentRule, is_active: e.target.checked})}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>取消</Button>
          <Button onClick={handleDialogSave} variant="contained" color="primary">
            保存
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* 刪除確認對話框 */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>確認刪除</DialogTitle>
        <DialogContent>
          <Typography>
            確定要刪除規則 "{ruleToDelete?.name}" 嗎？此操作無法撤銷。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>取消</Button>
          <Button onClick={confirmDeleteRule} color="error">
            刪除
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* 提示訊息 */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default AutoRecordConfig;
